import { useSelector } from 'react-redux';
import { getEnvValueFor } from '../../../../helpers/utils';
import type { RootState } from '../../../../state/store';
import { useMemo } from 'react';
import styled from 'styled-components';

// Generate masked data
const generateMockTableData = () => {
  const maskSymbol = '-';
  // Make first header "No." and second header "Date"
  const headers = ['#', 'Date', ...Array(10).fill(maskSymbol)];

  // Generate 25 rows of masked data
  const rows = [];

  // Calculate date for today
  const today = new Date();

  for (let i = 0; i < 25; i++) {
    const rowDate = new Date(today);
    // Each row's date is i days before today (most recent first)
    rowDate.setDate(today.getDate() - i);

    // Format date as MM/DD/YYYY
    const formattedDate = `${rowDate.getMonth() + 1}-${rowDate.getDate()}-${rowDate.getFullYear()}`;

    // First column is row number (i+1), second is date, rest are masked
    rows.push([String(i + 1), formattedDate, ...Array(10).fill(maskSymbol)]);
  }

  return { headers, rows };
};

const mockTableData = generateMockTableData();

const MaskedTable = () => {
  return (
    <TableWrapper>
      <table>
        <thead>
          <tr>
            {mockTableData.headers.map((header, index) => (
              <th key={`header-${index}`}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mockTableData.rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrapper>
  );
};

const MarketingLockedOverlay = ({ url }: { url: string | undefined }) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const iframeSrc = useMemo(() => {
    const version = getEnvValueFor('RELEASE_VERSION');
    if (url) {
      return `${url}&theme=${theme}&version=${version}`;
    }
    return null;
  }, [url, theme]);

  return (
    <OverlayContainer>
      <div className="table-container">
        <MaskedTable />
      </div>
      <div className="iframe-overlay">
        <iframe
          key={iframeSrc}
          title="widget locked"
          src={iframeSrc || ''}
          height={339}
          width={619}
          style={{
            top: 40,
            backgroundColor: 'transparent',
            border: '1px solid #transparent',
            borderRadius: '20px',
            boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.25)'
          }}
          allowTransparency
          frameBorder="0"
          scrolling="no"
          allow="allow-modal allow-popups allow-same-origin allow-top-navigation"
        />
      </div>
    </OverlayContainer>
  );
};

export default MarketingLockedOverlay;

const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  table {
    width: 100%;
    border-spacing: 0;
    overflow-x: scroll;
    background-color: ${({ theme }) => theme.widget.background};
    > thead {
      height: 55px;
      border-bottom: 1px solid #e4e4e4;
      > tr {
        > th {
          margin-left: 20px;
          font-size: 16px;
          color: ${({ theme }) => theme.text};
          font-weight: 700;
          position: relative;
          white-space: nowrap;
          padding: 0 0.5rem;
          > p {
            display: flex;
            align-items: center;
            margin-bottom: 0;
          }
          > div {
            position: absolute;
            top: 100%;
            background-color: #ffffff;
            border-color: #929292;
            border-style: solid;
            border-width: 0 1px 1px;
            left: -1px;
            padding: 0.5rem;
            > ul {
              list-style-type: none;
              padding: 0;
              margin: 0;
              > li {
                padding: 5px 0;
                label {
                  display: flex;
                  align-items: center;
                }
              }
            }
          }
        }
      }
      border-bottom: 1px solid #e4e4e4;
    }
    > tbody {
      > tr {
        > td {
          &.primary {
            color: #0094ff;
            font-weight: 700;
            cursor: pointer;
          }
          font-size: 15px;
          font-weight: 400;
          line-height: 21.48px;
          padding: 0 0.5rem;
          white-space: nowrap;
        }
        height: 49px;
        &:nth-child(odd) {
          background-color: ${({ theme }) => theme.body};
        }
      }
    }
  }
  background-color: transparent;
`;

const OverlayContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;

  .table-container {
    height: 100%;
    width: 100%;
  }

  .iframe-overlay {
    position: absolute;
    top: 30%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;
